<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="true"
                :showAddNew="true"
                title="IPD Pharmacy Statement"
                btnTitle="Details"
                extraBtnTitle="Summary"
                :showExtraBtn="true"
                @onClickAddNewButton="goToPrintDetails"
                @onClickCloseButton="closeButton"
                @onClickExtraBtn="goToPrint"
            />
        </div>

        <div v-if="patientDetails.patient" class="row p-1 px-2">
            <div class="col-md-4">
                <p>Patient Name: {{ patientDetails.patient.full_name }}</p>
                <p>Patient ID: {{ patientDetails.patient.patient_id }}</p>
                <p class="capitalize">
                    {{
                        (patientDetails.patient.birthday ? 'Age: ' + calculateAge(patientDetails.patient.birthday) : '') +
                        (patientDetails.patient.gender ? ' | Sex: ' + patientDetails.patient.gender : '')
                    }}
                </p>
                <p>Patient Phone No: {{ patientDetails.patient.mobile_no }}</p>
            </div>

            <div class="col-md-4">
                <p>Patient Address: {{ patientDetails.patient.full_address }}</p>
                <p>IPD admission no: {{ patientDetails.admission_no }}</p>
                <p>Admission date: {{ patientDetails.admission_date }}</p>
                <p>Admission Dept.: {{ patientDetails.department ? patientDetails.department.name : 'N/A' }}</p>
            </div>

            <div class="col-md-4">
                <p>Bed/Cabin no: {{ patientDetails.service_resource.name }}</p>
                <p>Referred Doctor: {{ patientDetails.human_resource ? patientDetails.human_resource.name : '' }}</p>
                <p>{{ patientDetails.consultant ? 'Consultant: ' + patientDetails.consultant.name : '' }}</p>
            </div>
        </div>

        <div class="px-2">
          <ul class="nav nav-tabs">
            <li v-for="tab in tabs" :key="tab.id" class="nav-item">
              <a
                  :class="['nav-link', { active: activeTab.id == tab.id }]"
                  @click="setActiveTab(tab)"
              >
                {{ tab.title }}
              </a>
            </li>
          </ul>
        </div>

        <div class="col-12 px-2 mt-2 mb-5">
            <component :is="tabComponents[activeTab.id]"
                :service="service"
                :receiptGenerals="receiptGenerals"
                :total="total"
                :patientDetails="patientDetails"
                :companyInfo="companyInfo"
                @onSaved="refreshStatement"
            />
        </div>

        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
    import TitleButton from '@/components/atom/TitleButton';
    import Statement from '@/components/molecule/company/hospital/ipd-pharmacy-bill/IpdPharmacyStatementTable.vue';
    import MoneyReceipts from "@/views/page/company/hospital/ipd-pharmacy-bill/IPDPharmacyMoneyReceipts.vue";
    import IPDPharmacyBillingList from '@/views/page/company/hospital/ipd-pharmacy-bill/IPDPharmacyBillingList.vue';
    import { useRoute, useRouter } from "vue-router";
    import { inject, onMounted, ref } from 'vue';
    import handleHospital from '@/services/modules/hospital';
    import Loader from '@/components/atom/LoaderComponent';
    import ipdStatementHelper from '@/services/utils/pdf/ipdStatementHelper';
    import handleCompany from "@/services/modules/company";
    import handleBarcode from "@/services/modules/barcode";
    import { useStore } from "vuex";
    import useDate from '@/services/utils/day';
    import ipdStatementDetails from '@/services/utils/pdf/ipdStatementDetailsMulti';

    const $route = useRoute();
    const $router = useRouter();

    const { fetchIpdStatement, calculateAge, fetchIpdStatementDetailsMulti } = handleHospital();
    const { generatePdf } = ipdStatementHelper();
    const { fetchCompanyInfo } = handleCompany();
    const { fetchBarcode } = handleBarcode();
    const { formatDate } = useDate();
    const { exportToPDF } = ipdStatementDetails();
    const store = useStore();

    const patientDetails = ref({});
    const service = ref([]);
    const loading = ref(false);
    const receiptGenerals = ref([]);
    const total = ref({});
    const companyInfo = ref({});

    const showError = inject('showError');

    const tabs = [
      { id: 'statement', title: 'Statements' },
      { id: 'ipd-bill', title: 'Bill' },
      { id: 'money-receipts', title: 'MoneyReceipts' },
    ];

    const tabComponents = {
      'statement': Statement,
      'money-receipts': MoneyReceipts,
      'ipd-bill': IPDPharmacyBillingList,
    };

    const activeTab = ref(tabs[0]);

    const getQuery = () => {
        const ipdRegisterId = $route.params.ipdRegisterId || $route.query.ipd_register_id;
        let query = `/${ipdRegisterId}?company_id=${$route.params.companyId}&sale_type=ipd_pharmacy_invoice`;
        return query;
    }

    const getStatement = () => {
        return fetchIpdStatement(getQuery()).then((res) => {
            patientDetails.value = res.data;
            service.value = res.service_data;
            receiptGenerals.value = res.receipt_generals;
            total.value = res.total;
            total.value.settlement_discount = res.data.settlement_discount;
        });
    }

    const refreshStatement = () => {
        loading.value = true;
        getStatement().catch(() => {
            showError('Something went wrong');
        }).finally(() => {
            loading.value = false;
        });
    }

    onMounted(() => {
        loading.value = true;

        Promise.all([
            getStatement(),
            fetchCompanyInfo($route.params.companyId).then((res) => {
                companyInfo.value = res.data
            })
        ]).catch(err => {
            showError('Something went wrong')
        }).finally(() => {
            loading.value = false;
        })
    })

    const goToPrint = () => {
        generatePdf(companyInfo.value, patientDetails.value, service.value, receiptGenerals.value, total.value)
    }

    const goToPrintDetails = async () => {
        const ipdRegisterId = $route.params.ipdRegisterId || $route.query.ipd_register_id;
        const productIds = service.value.map(item => item.id);
        const userName = store.state.user.user.name;
        let ipdRegisterDetails = {};
        let saleMastersGrouped = [];

        try {
            const query = `?company_id=${$route.params.companyId}&product_ids=${productIds.join(',')}`;
            const statementRes = await fetchIpdStatementDetailsMulti(ipdRegisterId, query);

            if(! statementRes.status) {
                return showError('Something went wrong');
            }

            ipdRegisterDetails = { ...statementRes.data };
            saleMastersGrouped = statementRes.sale_masters_grouped;

            let qrcodeData = JSON.stringify({
                ref_no: ipdRegisterDetails.admission_no,
                ref_date: ipdRegisterDetails.admission_date,
                created_by: userName,
                created_date_time: formatDate(new Date(ipdRegisterDetails.created_at))
            })

            var barCodeQuery = `?code=${ipdRegisterDetails.patient_serial_no}&qrcode=${qrcodeData}`;

            const barcodeRes = await fetchBarcode(barCodeQuery);

            if(! barcodeRes.status) {
                return showError('Something went wrong');
            }

            const barcode = barcodeRes.barcode;
            const qrcode = barcodeRes.qrcode;

            await exportToPDF(companyInfo.value, ipdRegisterDetails, saleMastersGrouped, userName, barcode, qrcode, receiptGenerals.value, total.value)
        } catch (e) {
            showError('Something went wrong');
        }
    }

    const closeButton = () => {
        $router.push({
            name: 'ipd-pharmacy-bill',
            params: {
                companyId: $route.params.companyId,
                moduleId: $route.params.moduleId,
                menuId: $route.params.menuId,
                pageId: $route.params.pageId
            }
        });
    }

    const setActiveTab = (tab) => {
        activeTab.value = tab;
        const routerObject = {
            path: $route.path,
            query: {
              ...$route.query,
              tab: tab.id
            }
        };

        if ($route.hash) {
            routerObject.hash = $route.hash;
        }

        $router.replace(routerObject);
    };

    onMounted(() => {
        const currentTab = $route.query.tab || 'statement';

        if(! currentTab || ! tabs.map(tab => tab.id).includes(currentTab)) return;
        activeTab.value = tabs.find(tab => tab.id === currentTab);
    })
</script>
